import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Loader2, AlertCircle, ImagePlus, X } from "lucide-react";
import { track } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-ink/70 px-4 py-3.5 text-sm sm:text-base text-slate-100 placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-accent/60";
const labelCls = "mb-2 block font-mono text-[10px] tracking-[0.24em] uppercase text-mute";

export const EstimateForm = ({ compact = false, formType = "estimate", testid = "estimate" }) => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", vehicle: "", carrier: "",
    contactMethod: "Phone", message: "", company: "",
  });
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos((p) => [...p, ...files].slice(0, 6));
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("type", formType);
      photos.forEach((p) => fd.append("photos", p));
      const res = await fetch(`${API}/estimate`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("send failed");
      track("form_submit", { form: formType });
      navigate("/thank-you");
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong — please call (214) 888-2886 instead.");
    }
  };

  return (
    <form data-testid={`${testid}-form`} onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div>
        <label htmlFor={`${testid}-name`} className={labelCls}>Full Name *</label>
        <input id={`${testid}-name`} data-testid={`${testid}-name-input`} required value={form.name} onChange={set("name")} placeholder="James Hunt" className={inputCls} maxLength={120} />
      </div>
      <div>
        <label htmlFor={`${testid}-phone`} className={labelCls}>Phone *</label>
        <input id={`${testid}-phone`} data-testid={`${testid}-phone-input`} type="tel" required value={form.phone} onChange={set("phone")} placeholder="(214) 555-0100" className={inputCls} maxLength={40} />
      </div>
      <div>
        <label htmlFor={`${testid}-email`} className={labelCls}>Email *</label>
        <input id={`${testid}-email`} data-testid={`${testid}-email-input`} type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
      </div>
      <div>
        <label htmlFor={`${testid}-vehicle`} className={labelCls}>Vehicle (Year / Make / Model) *</label>
        <input id={`${testid}-vehicle`} data-testid={`${testid}-vehicle-input`} required value={form.vehicle} onChange={set("vehicle")} placeholder="2022 Porsche 911 Carrera" className={inputCls} maxLength={120} />
      </div>
      {!compact && (
        <>
          <div>
            <label htmlFor={`${testid}-carrier`} className={labelCls}>Insurance Carrier (optional)</label>
            <input id={`${testid}-carrier`} data-testid={`${testid}-carrier-input`} value={form.carrier} onChange={set("carrier")} placeholder="e.g. State Farm" className={inputCls} maxLength={80} />
          </div>
          <div>
            <label htmlFor={`${testid}-method`} className={labelCls}>Preferred Contact Method</label>
            <select id={`${testid}-method`} data-testid={`${testid}-method-select`} value={form.contactMethod} onChange={set("contactMethod")} className={inputCls}>
              <option>Phone</option>
              <option>Text</option>
              <option>Email</option>
            </select>
          </div>
        </>
      )}
      <div className="sm:col-span-2">
        <label htmlFor={`${testid}-message`} className={labelCls}>Damage Description *</label>
        <textarea id={`${testid}-message`} data-testid={`${testid}-message-input`} required rows={compact ? 3 : 4} value={form.message} onChange={set("message")} placeholder="Rear bumper and quarter panel damage, other driver insured by…" className={`${inputCls} resize-none`} maxLength={4000} />
      </div>
      <div className="sm:col-span-2">
        <span className={labelCls}>Photos of the Damage (up to 6)</span>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} data-testid={`${testid}-photo-input`} />
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid={`${testid}-photo-button`}
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-xl border border-dashed border-white/20 bg-ink/50 px-5 py-3.5 text-sm text-slate-300 transition-colors hover:border-accent/50 hover:text-accent"
          >
            <ImagePlus size={16} />
            {photos.length ? "Add more photos" : "Upload photos"}
          </button>
          {photos.map((p, i) => (
            <span key={`${p.name}-${i}`} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-surface px-3 py-1.5 text-xs text-slate-400">
              {p.name.length > 22 ? `${p.name.slice(0, 19)}…` : p.name}
              <button type="button" aria-label={`Remove ${p.name}`} onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400">
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      </div>
      <div className="hidden" aria-hidden="true">
        <label htmlFor={`${testid}-company`}>Company</label>
        <input id={`${testid}-company`} tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} />
      </div>
      <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <button
          type="submit"
          data-testid={`${testid}-submit-button`}
          disabled={status === "sending"}
          className="group inline-flex items-center gap-3 rounded-full bg-accentdeep px-8 py-4 font-display font-bold text-sm sm:text-base text-white transition-all duration-300 hover:bg-accenthover hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
        >
          {status === "sending" ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} strokeWidth={2.2} />}
          {status === "sending" ? "Sending…" : "Get a Free Estimate"}
        </button>
        {status === "error" && (
          <p data-testid={`${testid}-form-error`} className="inline-flex items-center gap-2 text-sm text-red-400">
            <AlertCircle size={15} />
            {errorMsg}
          </p>
        )}
      </div>
    </form>
  );
};
